/**
 * PATH: packages/bills/src/client.tsx
 * FILE_ID: BILLS-PKG-CLIENT-001
 * PURPOSE: Dependency-injected data layer for the Bills feature.
 *
 * WHY: `apps/billing` uses axios via `@quotech/api`; `apps/sam` uses
 *      its own `quotechFetch` wrapper that injects a `X-Tenant-Id`
 *      header. The shared workspace UI in this package must be
 *      transport-agnostic — it cannot import either app's stack.
 *
 *      Solution: each app constructs an object that satisfies the
 *      `BillsClient` interface and passes it via `<BillsProvider>`.
 *      The shared workspace calls `useBillsClient()` to access it.
 *
 * FLOW:
 *   apps/billing/main.tsx
 *     └─ <BillsProvider client={createAxiosBillsClient()}>
 *          └─ <BookkeepingWorkspace />     (this package)
 *               └─ useBillsClient().list(...)   (calls injected impl)
 *
 *   apps/sam/main.tsx
 *     └─ <BillsProvider client={createSamBillsClient()}>
 *          └─ <BookkeepingWorkspace />     (same package)
 *               └─ useBillsClient().list(...)   (different impl, same shape)
 *
 * DEPENDENCIES: react only. Do not import from @quotech/api or any
 *               app-specific module — that would defeat the purpose.
 */

import { createContext, useContext, type ComponentType, type ReactNode } from 'react';
import type {
  BillActionOptions,
  BillDetail,
  BillEditablePatch,
  BillEmailDetail,
  BillManualCreatePayload,
  BillPoAllocation,
  BillPoAllocationsResponse,
  BillPoSuggestionsResponse,
  BillsExportFormat,
  BillFlag,
  BillListOrder,
  BillsListResponse,
  BillSource,
  BillStats,
  BillStatus,
  BillStatusFilter,
  BillSupplierOption,
  BillPaymentSuggestionsResponse,
  ChartOfAccountsResponse,
  MarkBillPaidInput,
  SetSupplierTermInput,
  SupplierCreateInput,
  ConvertEmailToPoInput,
  CreateBillPoAllocationInput,
  CreatePoPaymentInput,
  CreatePurchaseOrderInput,
  ExtractPendingResponse,
  PaymentRequestsResponse,
  PoPayment,
  PoPaymentsResponse,
  PurchaseOrderDetail,
  PurchaseOrdersListResponse,
  UpdatePurchaseOrderInput,
  XeroPaymentSuggestionsResponse,
} from './types';

/**
 * The full surface every Bills consumer needs. Each method returns a
 * Promise of the corresponding response type. Errors should be thrown
 * (rejected) so the caller can show a toast / inline message.
 */
export interface BillsClient {
  list(filter?: {
    status?: BillStatusFilter;
    source?: BillSource;
    has_xero?: boolean;
    /** Server-side search (P1-1+). Optional — impls may ignore it. */
    q?: string;
    /** Attention-reason filter (queue redesign). Optional — impls that
     * predate it just ignore it and the chips degrade to status-only. */
    flag?: BillFlag;
    /** ``triage`` = overdue-first operator order (queue redesign). */
    order?: BillListOrder;
    /** Page size for infinite scroll (P1-1+). Omit for the full page. */
    limit?: number;
    /** Zero-based offset for infinite scroll (P1-1+). */
    offset?: number;
  }): Promise<BillsListResponse>;
  /**
   * Create a manually-entered bill (no upstream email). Posted by
   * the BillDrawer in create mode for tenants without a connected
   * mailbox / without Xero. Returns the new `BillDetail`. Added
   * 2026-05-17 with the manual-bill + Xero-export feature.
   */
  create(payload: BillManualCreatePayload): Promise<BillDetail>;
  /**
   * Stream a CSV of bills in the requested format. Apps that proxy
   * downloads (apps/sam, apps/newmark) implement this as a fetch +
   * Blob; the consumer is expected to wire up a hidden anchor /
   * `<a download>` to actually trigger the file save.
   */
  exportCsv(
    format: BillsExportFormat,
    filter?: { status?: BillStatus; source?: BillSource },
  ): Promise<Blob>;
  detail(billId: string): Promise<BillDetail>;
  update(billId: string, patch: BillEditablePatch): Promise<BillDetail>;
  stats(): Promise<BillStats>;
  /**
   * Fetch the org's Chart of Accounts for the per-line allocation grid's
   * CoA picker (p3b). OPTIONAL + additive — apps that don't wire it just
   * get a free-text account-code cell instead of the grouped picker.
   * Pass ``tree`` to also receive the derived class→group→account
   * hierarchy. Backend: GET /api/cfo/chart-of-accounts/.
   */
  getChartOfAccounts?(opts?: {
    tree?: boolean;
    search?: string;
  }): Promise<ChartOfAccountsResponse>;
  /**
   * List the org's suppliers (quotebuilder.Supplier) for the Vendor
   * supplier-picker on the bill panel. OPTIONAL — apps that haven't wired
   * it yet still compile and the picker degrades to the plain vendor_name
   * text inputs. ``search`` filters server-side by name/code/email.
   * Returns the SAME supplier list that Xero contact sync, the email
   * "Add as Supplier" action, and manual entry all populate.
   */
  listSuppliers?(search?: string): Promise<BillSupplierOption[]>;
  /**
   * Create a canonical org Supplier (quotebuilder.Supplier) from a bill's
   * extracted vendor, then return it as a `BillSupplierOption` ready to link.
   * OPTIONAL + additive — apps that don't wire it just hide the picker's
   * inline "+ Create '<vendor>'" affordance (the empty state still points the
   * user at Xero sync / the email "Add as Supplier" action). Dedupe is
   * operator-driven: the picker surfaces existing matches first and only
   * offers create when none has the exact name, so this never silently mints
   * a duplicate of a supplier already on screen.
   */
  createSupplier?(input: SupplierCreateInput): Promise<BillSupplierOption>;
  /**
   * ``opts.force`` re-submits past the backend's 409
   * ``possible_duplicate`` guard once the operator has confirmed the
   * duplicate dialog. Implementations that ignore it still compile —
   * the guard then simply can't be overridden from that app.
   */
  approve(billId: string, opts?: BillActionOptions): Promise<BillDetail>;
  reject(billId: string, reason?: string): Promise<BillDetail>;
  linkJob(billId: string, jobId: number | null): Promise<BillDetail>;
  pushToXero(billId: string, opts?: BillActionOptions): Promise<BillDetail>;
  /**
   * Restore a cascade fail-closed bill (quarantined / awaiting_document /
   * needs_fetch / needs_review) back to its pre_quarantine_status. Returns
   * the updated BillDetail. OPTIONAL + additive — embedders that haven't
   * wired it (apps/billing) just hide the Restore affordance; the shared
   * workspace renders it only when this method is present.
   */
  restore?(billId: string): Promise<BillDetail>;
  /**
   * Set the bill's LINKED supplier's internal payment term and re-derive this
   * bill's due date from ``invoice_date + term``. Returns the updated
   * `BillDetail` (new ``due_date`` + ``predicted_due_date`` +
   * ``supplier_detail.payment_term_*``).
   *
   * OPTIONAL + additive — apps that haven't wired it still compile; the panel
   * hides the payment-terms selector when undefined. The bill must already
   * have a linked supplier (the backend 400s otherwise — the picker is the
   * prerequisite affordance).
   */
  setSupplierTerm?(billId: string, input: SetSupplierTermInput): Promise<BillDetail>;
  /**
   * Mark the bill as already paid, or clear it. Returns the updated
   * `BillDetail` (``payment_status`` + captured ``paid_*`` fields).
   * OPTIONAL — the panel hides the "Mark as paid" affordance when undefined.
   */
  markPaid?(billId: string, input: MarkBillPaidInput): Promise<BillDetail>;
  /**
   * Fetch bank-feed match candidates for the bill. STUB on the backend until a
   * feed is connected (returns ``{ suggestions: [], bank_feed_connected:
   * false, … }``). OPTIONAL — the panel hides the "Search bank feeds" button
   * when undefined.
   */
  getBillPaymentSuggestions?(billId: string): Promise<BillPaymentSuggestionsResponse>;
  extractFromEmail(emailId: string): Promise<BillDetail>;
  extractPending(opts?: { limit?: number; force?: boolean }): Promise<ExtractPendingResponse>;
  /**
   * Reclassify the email backing a bill — typical use is "this isn't
   * a bill, it's our outgoing invoice" or "this is spam, move it out
   * of the AP queue". Wraps the bulk-action endpoint
   * (POST /api/emails/bulk/, action=set_category) so it creates a
   * locked EmailCategoryOverride; the AI classifier won't undo it on
   * its next pass. Once the email's category leaves 'bill' the
   * Bills list view will sweep the orphaned ExtractedBill row on its
   * next refresh (see BillListView._cleanup_orphaned_pending).
   *
   * Returns void — the caller refetches the list to see the bill
   * disappear; no detail to refresh because the row is being removed.
   */
  setEmailCategory(emailId: string, category: string): Promise<void>;

  /**
   * Resolve a downloadable URL for a bill's source attachment, or null
   * if the app doesn't know how to. Each app maps `attachment_id` to its
   * own download/proxy route. Kept synchronous because the URL is a
   * lookup, not a fetch.
   *
   * Use this when the app's auth cookies cover the `<iframe>`/`<img>`
   * load (apps/billing pattern). When auth is bearer-only and won't
   * ride along with element src loads, implement `fetchAttachmentBlob`
   * instead and the viewer will turn the result into a blob: URL.
   */
  attachmentUrl?(attachmentId: string | null | undefined): string | null;

  /**
   * Async fetch the attachment as a Blob. The viewer converts the blob
   * to an ``object URL`` for inline preview and revokes it on cleanup,
   * so this method does not need to manage URL lifecycle.
   *
   * Apps that authenticate with a bearer header (apps/sam pattern) MUST
   * use this — the browser cannot attach the header to a passive
   * `<iframe src>` load, so the request would 401.
   *
   * Return `null` if the bill has no attachment or this app cannot
   * resolve it. Throw on transport errors so the viewer can surface
   * them.
   */
  fetchAttachmentBlob?(attachmentId: string | null | undefined): Promise<Blob | null>;

  /**
   * Resolve the source email of a bill so the workspace's "Email" tab
   * can render the full message body, recipient list, and attachments
   * without yanking the user out to the Email Hub.
   *
   * Optional — apps that haven't wired it up yet just hide the tab's
   * full-body section and fall back to the bill's `email_subject` /
   * `email_body_preview` summary fields.
   */
  fetchEmailDetail?(emailId: string | null | undefined): Promise<BillEmailDetail | null>;

  /**
   * Sanitise an HTML string for safe inline rendering. The package keeps
   * zero runtime deps, so each app injects its own DOM sanitiser
   * (DOMPurify in apps/sam, the same in apps/billing). When undefined
   * the email panel falls back to plain-text rendering.
   */
  sanitiseHtml?(html: string): string;

  /**
   * Render the bill's source email inline. OPTIONAL but PREFERRED — when an
   * app injects its canonical Email Hub reader here, the workspace's Email
   * tab + the centre "Source email" split REUSE that exact component
   * instead of the package's bespoke fallback reader.
   *
   * Tenant apps wire ``@quotech/bills/feature/BillEmailHubReader`` — the
   * SAME full Email Hub reader the kanban card drawer's Email tab mounts
   * (search-in-email, tags, right-click context menu, smart actions,
   * attachment preview) — so there is ONE email reader, no forks. Keeping it
   * injected preserves the core's zero-app-dependency contract: the
   * email-hub-backed component lives in the app-facing ``feature/`` layer,
   * never in ``components/``.
   *
   * Tenant scoping: the injected viewer (useEmail → emailApi) reads the
   * email-hub tenant id, which the bills client's ``setBillsTenantId`` now
   * sets in lock-step — so the email loads in the workspace even when the
   * user never opened the Email Hub this session.
   *
   * When undefined the panel falls back to ``fetchEmailDetail`` + a plain
   * reader (apps/billing, which has no email-hub wiring).
   */
  EmailView?: ComponentType<{
    emailId: string | null;
    onPreviewAttachment?: (idx: number) => void;
  }>;

  /**
   * Convert a Word document (`.docx`) blob to HTML for inline preview.
   *
   * Why injected: the package is intentionally zero-runtime-deps. The
   * conversion engine (mammoth.js, ~80KB gzip) is loaded by the host
   * app — typically lazily, so apps that never preview Word bills
   * never pay the bundle cost. Apps that don't implement this method
   * see a "Download original" fallback in the viewer.
   *
   * The returned HTML SHOULD already inline embedded images (e.g. via
   * mammoth's `images.dataUri` converter) so the rendered preview
   * survives revocation of the source blob URL. The viewer will still
   * pass the HTML through `sanitiseHtml` before rendering, so callers
   * MUST NOT rely on this method for security — it is a renderer, not
   * a sanitiser.
   *
   * `messages` carries non-fatal warnings from the converter (unmapped
   * styles, unsupported elements) for debugging; the viewer ignores
   * them by default.
   */
  convertDocxToHtml?(blob: Blob): Promise<{
    html: string;
    messages?: Array<{ type: string; message: string }>;
  }>;

  /* ──────────────────────────────────────────────────────────────────
   * Purchase orders + bill ↔ PO allocation
   *
   * All methods are OPTIONAL so apps that haven't wired the PO panel
   * yet still compile. The right-rail PO tab gracefully hides each
   * affordance whose method is undefined (e.g. apps without
   * `createPurchaseOrder` lose the "Create PO" form but can still
   * browse + allocate the existing ones).
   *
   * Backend contract: organisation/views/po_views/ (see endpoints.ts).
   * ────────────────────────────────────────────────────────────────── */

  /** GET — POs for a job (right-rail "available POs" list). */
  listPurchaseOrdersForJob?(
    jobId: number,
    opts?: { include_billed?: boolean },
  ): Promise<PurchaseOrdersListResponse>;

  /** GET — search POs across the org (used by the "find a PO" picker). */
  listPurchaseOrders?(
    filter?: {
      status?: string;
      job?: number;
      supplier?: string;
      search?: string;
      source?: string;
      unallocated?: boolean;
    },
  ): Promise<PurchaseOrdersListResponse>;

  /** POST — create a manual PO. */
  createPurchaseOrder?(input: CreatePurchaseOrderInput): Promise<PurchaseOrderDetail>;

  /** PATCH — update a manual / email-extracted PO. */
  updatePurchaseOrder?(
    poId: number,
    patch: UpdatePurchaseOrderInput,
  ): Promise<PurchaseOrderDetail>;

  /** DELETE — remove a manual / email-extracted PO. */
  deletePurchaseOrder?(poId: number): Promise<void>;

  /** GET — bill's existing PO allocations + reconciliation summary. */
  listBillPoAllocations?(billId: string): Promise<BillPoAllocationsResponse>;

  /**
   * POST — create / upsert a bill ↔ PO allocation. Idempotent on the
   * (billId, purchase_order_id) pair: re-posting updates the existing
   * row's allocated_amount + line_matches + notes rather than creating
   * a duplicate. Backed by a DB-level UniqueConstraint.
   */
  upsertBillPoAllocation?(
    billId: string,
    input: CreateBillPoAllocationInput,
  ): Promise<BillPoAllocation>;

  /** DELETE — unallocate a specific PO from a bill. */
  deleteBillPoAllocation?(billId: string, allocationId: string): Promise<void>;

  /** GET — ranked auto-match suggestions for the bill (top 10). */
  getBillPoSuggestions?(billId: string): Promise<BillPoSuggestionsResponse>;

  /* ──────────────────────────────────────────────────────────────────
   * PO payments + convert-email-to-PO + payment-requests inbox
   * (PR-2, 2026-05-05)
   *
   * All optional — embedders that haven't wired the payment side yet
   * still compile. The right-rail Payments section, the Convert
   * button, and the "POs awaiting payment" lane each gracefully
   * degrade when their corresponding method is undefined.
   * ────────────────────────────────────────────────────────────────── */

  /** GET — payments recorded against a PO + the running balance. */
  listPoPayments?(poId: number): Promise<PoPaymentsResponse>;

  /** POST — record a new payment against a PO. */
  recordPoPayment?(
    poId: number,
    input: CreatePoPaymentInput,
  ): Promise<{
    payment: PoPayment;
    amount_paid: string;
    payment_status: string;
  }>;

  /** DELETE — remove a payment (returns the recomputed balance). */
  deletePoPayment?(
    poId: number,
    paymentId: string,
  ): Promise<{ amount_paid: string; payment_status: string }>;

  /** GET — ranked unmatched XeroPayments that could clear this PO. */
  getPoPaymentSuggestions?(poId: number): Promise<XeroPaymentSuggestionsResponse>;

  /** POST — convert an inbound email to a PO. */
  convertEmailToPo?(
    emailId: string,
    input: ConvertEmailToPoInput,
  ): Promise<PurchaseOrderDetail>;

  /**
   * GET — "POs awaiting payment" inbox lane: emails categorised as
   * payment_request (and quote_submission when ``include_quotes``).
   */
  listPaymentRequests?(opts?: {
    include_quotes?: boolean;
  }): Promise<PaymentRequestsResponse>;
}

const BillsClientContext = createContext<BillsClient | null>(null);

interface BillsProviderProps {
  client: BillsClient;
  children: ReactNode;
}

export function BillsProvider({ client, children }: BillsProviderProps) {
  return <BillsClientContext.Provider value={client}>{children}</BillsClientContext.Provider>;
}

/**
 * Read the BillsClient from context. Throws if no provider above.
 * The throw is intentional — the app forgot to wire its data layer.
 */
export function useBillsClient(): BillsClient {
  const client = useContext(BillsClientContext);
  if (!client) {
    throw new Error(
      '[@quotech/bills] useBillsClient called outside of <BillsProvider>. ' +
      'Wrap your route tree with <BillsProvider client={yourClient}> in main.tsx.',
    );
  }
  return client;
}
